<template>
  <v-text-field
    v-model="inputValue"
    :label="label"
    :placeholder="placeholder"
    :disabled="disabled"
    :readonly="readonly"
    :variant="variant"
    :density="density"
    :class="['number-input', customClass]"
    hide-details="auto"
    :hint="hint"
    :error-messages="errorMessages"
    :rules="rules"
    :min="min"
    :max="max"
    :step="step"
    type="number"
    inputmode="numeric"
    :autocomplete="autocomplete"
    @update:model-value="handleInput"
    @blur="handleBlur"
  >
    <template v-slot:prepend-inner>
      <v-btn
        :variant="buttonVariant"
        :density="buttonDensity"
        icon="mdi-minus"
        @click="decrement"
        :disabled="isDecrementDisabled || disabled || readonly"
        :color="buttonColor"
        :size="buttonSize"
        class="decrement-btn"
      ></v-btn>
    </template>
    
    <template v-slot:append-inner>
      <v-btn
        :variant="buttonVariant"
        :density="buttonDensity"
        icon="mdi-plus"
        @click="increment"
        :disabled="isIncrementDisabled || disabled || readonly"
        :color="buttonColor"
        :size="buttonSize"
        class="increment-btn"
      ></v-btn>
    </template>
  </v-text-field>
</template>

<script setup>
import { ref, watch, computed } from 'vue';

const props = defineProps({
  modelValue: {
    type: Number,
    default: 0
  },
  label: {
    type: String,
    default: ''
  },
  placeholder: {
    type: String,
    default: ''
  },
  min: {
    type: Number,
    default: undefined
  },
  max: {
    type: Number,
    default: undefined
  },
  step: {
    type: Number,
    default: 1
  },
  precision: {
    type: Number,
    default: 0 // Number of decimal places
  },
  disabled: {
    type: Boolean,
    default: false
  },
  readonly: {
    type: Boolean,
    default: false
  },
  variant: {
    type: String,
    default: 'outlined'
  },
  density: {
    type: String,
    default: 'compact'
  },
  autocomplete: {
    type: String,
    default: 'off'
  },
  customClass: {
    type: String,
    default: ''
  },
  hint: {
    type: String,
    default: ''
  },
  errorMessages: {
    type: [String, Array],
    default: ''
  },
  rules: {
    type: Array,
    default: () => []
  },
  // Button specific props
  buttonVariant: {
    type: String,
    default: 'text'
  },
  buttonDensity: {
    type: String,
    default: 'comfortable'
  },
  buttonColor: {
    type: String,
    default: 'primary'
  },
  buttonSize: {
    type: String,
    default: 'small'
  }
});

const emit = defineEmits(['update:modelValue', 'change', 'blur']);

const inputValue = ref(formatValue(props.modelValue));

watch(() => props.modelValue, (newVal) => {
  inputValue.value = formatValue(newVal);
});

const isDecrementDisabled = computed(() => {
  if (props.min === undefined) return false;
  return Number(inputValue.value) <= props.min;
});

const isIncrementDisabled = computed(() => {
  if (props.max === undefined) return false;
  return Number(inputValue.value) >= props.max;
});

function formatValue(value) {
  const numValue = Number(value);
  
  if (isNaN(numValue)) return 0;
  
  if (props.precision === 0) {
    return Math.floor(numValue);
  } else {
    return Number(numValue.toFixed(props.precision));
  }
}

function increment() {
  const currentValue = Number(inputValue.value);
  let newValue = currentValue + props.step;
  
  if (props.max !== undefined && newValue > props.max) {
    newValue = props.max;
  }
  
  const formattedValue = formatValue(newValue);
  inputValue.value = formattedValue;
  emit('update:modelValue', formattedValue);
  emit('change', formattedValue);
}

function decrement() {
  const currentValue = Number(inputValue.value);
  let newValue = currentValue - props.step;
  
  if (props.min !== undefined && newValue < props.min) {
    newValue = props.min;
  }
  
  const formattedValue = formatValue(newValue);
  inputValue.value = formattedValue;
  emit('update:modelValue', formattedValue);
  emit('change', formattedValue);
}

function handleInput(value) {
  const numValue = Number(value);
  
  if (isNaN(numValue)) {
    inputValue.value = 0;
    emit('update:modelValue', 0);
    return;
  }
  
  let processedValue = numValue;
  
  if (props.min !== undefined && processedValue < props.min) {
    processedValue = props.min;
  }
  
  if (props.max !== undefined && processedValue > props.max) {
    processedValue = props.max;
  }
  
  // Format and emit
  const formattedValue = formatValue(processedValue);
  inputValue.value = formattedValue;
  emit('update:modelValue', formattedValue);
}

function handleBlur() {
  const formattedValue = formatValue(inputValue.value);
  inputValue.value = formattedValue;
  emit('blur', formattedValue);
}
</script>

<style scoped>
.number-input :deep(.v-field__prepend-inner),
.number-input :deep(.v-field__append-inner) {
  margin-inline-start: 0;
  margin-inline-end: 0;
  padding-inline-start: 0;
  padding-inline-end: 0;
}

.number-input :deep(.v-field__input) {
  text-align: center;
}

.decrement-btn, 
.increment-btn {
  height: 100%;
  border-radius: 0;
}

.number-input :deep(.v-field__input) {
  margin-inline-start: 0 !important;
  margin-inline-end: 0 !important;
}

/* Hide default number input arrows */
.number-input :deep(input[type="number"]) {
  -moz-appearance: textfield;
  appearance: textfield;
}

.number-input :deep(input[type="number"]::-webkit-inner-spin-button),
.number-input :deep(input[type="number"]::-webkit-outer-spin-button) {
  -webkit-appearance: none;
  margin: 0;
}
</style>